import { useEffect, useState } from 'react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { LocationPhoneSelection, PersonPhoneSelection } from '@frontend/thread-header-context';
import { usePreferredOutboundNumberUserSettings } from '@frontend/use-preferred-outbound-number-user-settings';
import { usePopoverMenu } from '@frontend/design-system';
import { ActiveConversations } from './active-conversations';
import { LocationPhoneSelectionPopoverSubmenu } from './location-phone-selection-popover-submenu';
import { LocationsOptions } from './locations-options';
import { useBatchGetThreadStatusQuery } from './use-batch-get-thread-status-query';
import { useOutboundNumberSelectorView } from './use-outbound-number-selector-view';

type LocationPhoneSelectionPopoverMenuContentProps = {
  disableAutoThreadOpen?: boolean;
  selectedLocationPhone?: LocationPhoneSelection;
  selectedPersonPhone?: PersonPhoneSelection;
  groupParticipants?: string[];
  isGroup?: boolean;
  onSelectLocationPhone: (locationPhone: string, locationId: string, threadId?: string) => void;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  activeIndex: number;
  hideCurrentLocationPhone?: boolean;
  isOpen: boolean;
  /** When true, skips "Recent Activity" view and shows all numbers directly with search */
  isNewThreadCreation?: boolean;
};

/** A version of the `FromSelectionPopoverMenu` component with departments deprecated */
export const LocationPhoneSelectionPopoverMenuContents = ({
  disableAutoThreadOpen = false,
  selectedLocationPhone,
  selectedPersonPhone,
  groupParticipants,
  isGroup,
  onSelectLocationPhone,
  getItemProps,
  activeIndex,
  isOpen,
  isNewThreadCreation = false,
}: LocationPhoneSelectionPopoverMenuContentProps) => {
  const { t } = useTranslation('inbox');
  const { getScopeName, selectedLocationIds } = useAppScopeStore();
  const locationIdsWithACL = filterLocationIdsByACLs({
    locationIds: selectedLocationIds,
    acls: Permission.SMS_INBOX_MANAGE,
  });
  const isMultiLocation = locationIdsWithACL.length > 1;
  const {
    activeConversationCount,
    activeConversationsByLocation,
    allPhoneOptionsByLocation,
    filteredLocationPhonesByLocation,
    hasActiveConversation,
    locationPhonesQuery,
  } = useBatchGetThreadStatusQuery({
    personPhone: selectedPersonPhone?.personPhone,
    groupParticipants,
    isGroup,
  });

  // Get enabled outbound phones count
  const enabledPhonesCount = locationPhonesQuery.enabledData.allLocationPhones.length;
  const hasEnabledOutboundPhones = enabledPhonesCount > 0;

  const { initializeView, setView, view } = useOutboundNumberSelectorView();
  useEffect(() => {
    // just make sure the view is initialized when opening the menu (there are multiple ways to close the menu)
    if (isOpen) {
      initializeView({
        hasActiveConversation: disableAutoThreadOpen ? false : hasActiveConversation,
        isMultiLocation,
        isNewThreadCreation,
        hasEnabledOutboundPhones,
      });
      setSelectedLocationId('');
    }
  }, [
    disableAutoThreadOpen,
    hasActiveConversation,
    hasEnabledOutboundPhones,
    initializeView,
    isMultiLocation,
    isNewThreadCreation,
    isOpen,
  ]);

  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  const { onClick: openThread } = useMessageAction();

  const { hasPreferredOutboundNumberInteraction, setPreferredOutboundNumberForLocation } =
    usePreferredOutboundNumberUserSettings();

  // Get all enabled outbound SMS phone numbers as a flat array for quick lookup
  const enabledLocationPhones = locationPhonesQuery.enabledData.allLocationPhones.map(
    (lp: { locationPhone: string }) => lp.locationPhone
  );

  // active-conversations view = shows list of active conversations with the option to see all numbers
  if (view === 'active-conversations') {
    return (
      <ActiveConversations
        activeConversationCount={activeConversationCount}
        activeIndex={activeIndex}
        conversationsByLocation={activeConversationsByLocation}
        enabledLocationPhones={enabledLocationPhones}
        getItemProps={getItemProps}
        locationPhones={filteredLocationPhonesByLocation}
        onAllOutboundNumbersClick={() => setView(isMultiLocation ? 'all-locations' : 'all-numbers')}
        onSelectConversation={onSelectLocationPhone}
        selectedLocationPhone={selectedLocationPhone?.locationPhone}
      />
    );
  }

  // all-locations view = shows list of all location names (multi-location only)
  if (view === 'all-locations') {
    return (
      <LocationsOptions
        getItemProps={getItemProps}
        locationOptions={Object.keys(allPhoneOptionsByLocation || {}).sort((a, b) =>
          getScopeName(a).localeCompare(getScopeName(b))
        )}
        onBack={() => setView('active-conversations')}
        onSelectLocation={(locationId) => {
          setSelectedLocationId(locationId);
          if (disableAutoThreadOpen) {
            setView('all-numbers');
            return;
          }

          // if this comes from the new create thread flow (i.e. the user hasn't selected a location for the person phone yet), let this hook handle which location phone number to select
          if (!selectedLocationPhone?.locationPhone) {
            openThread({
              openInInbox: true,
              personPhone: selectedPersonPhone?.personPhone,
              threadGroupId: locationId,
            });
            return;
          }
          // when the user has already selected a location phone and they are going through this flow to update it, don't select a location phone automatically
          setView('all-numbers');
        }}
        selectedLocationPhone={selectedLocationPhone}
        showHeader={hasActiveConversation}
      />
    );
  }

  const appliedSelectedLocationId = (isMultiLocation ? selectedLocationId : locationIdsWithACL[0]) || '';

  // Get enabled outbound SMS phones for the selected location
  const enabledPhonesForLocation =
    locationPhonesQuery.enabledData.locationPhonesByLocation[appliedSelectedLocationId] || [];

  // In "All Outbound Numbers" view, show ONLY phones that are outbound SMS enabled
  // This fixes the bug where non-enabled phones were shown when no prior conversations exist
  const selectedLocationIdPhoneOptions = enabledPhonesForLocation;
  const defaultSmsOptionForLocation = selectedLocationIdPhoneOptions.find(
    (lp: InboxLocationPhonesTypes.LocationPhone) => lp.defaultSms && lp.locationId === appliedSelectedLocationId
  );

  // Auto-initialize preferred outbound number to the location's default SMS number
  // ONLY if the user has never interacted with the preference for this location.
  // hasPreferredOutboundNumberInteraction returns true if user has set OR explicitly cleared,
  // ensuring we don't re-set a preference the user intentionally removed.
  if (
    appliedSelectedLocationId &&
    !hasPreferredOutboundNumberInteraction(appliedSelectedLocationId) &&
    defaultSmsOptionForLocation
  ) {
    setPreferredOutboundNumberForLocation(appliedSelectedLocationId, defaultSmsOptionForLocation.locationPhone);
  }

  // all-numbers view = show all numbers for a location name
  return (
    <LocationPhoneSelectionPopoverSubmenu
      isNewThreadCreation={isNewThreadCreation}
      getItemProps={getItemProps}
      hasActiveConversation={hasActiveConversation}
      onBack={() => {
        // go back to all-locations view if multi-location
        if (isMultiLocation) {
          setView('all-locations');
          setSelectedLocationId('');
          return;
        }

        // change back to active-conversations view if there are any active conversation
        if (hasActiveConversation) {
          setView('active-conversations');
          return;
        }
      }}
      onSelectLocationPhone={onSelectLocationPhone}
      phonesByLocation={allPhoneOptionsByLocation}
      phoneOptions={selectedLocationIdPhoneOptions}
      showHeader={isMultiLocation || hasActiveConversation}
      selectedLocationPhone={selectedLocationPhone?.locationPhone}
      title={isMultiLocation ? getScopeName(appliedSelectedLocationId) : t('All Outbound Numbers')}
    />
  );
};
